import {
  createSignedStorageFullImageUrl,
  createSignedStorageThumbnailUrl,
  getStorageStableKey,
  resolveStorageObjectRef,
} from "@/lib/storageImageUrls";

export const IMPORT_PHOTO_BUCKET = "job-import-photos";
export const IMPORT_PHOTO_SIGNED_URL_TTL_SECONDS = 60 * 60;
const IMPORT_PHOTO_SIGNED_URL_REFRESH_MARGIN_MS = 5 * 60 * 1000;

type SignedImportPhotoUrlCacheEntry = {
  signedUrl: string;
  expiresAtMs: number;
};

const signedImportPhotoUrlCache = new Map<string, SignedImportPhotoUrlCacheEntry>();

export function extractImportPhotoStoragePath(photoRef: string): string | null {
  const ref = resolveStorageObjectRef(photoRef, IMPORT_PHOTO_BUCKET);
  if (!ref || ref.bucket !== IMPORT_PHOTO_BUCKET) return null;
  return ref.path;
}

export function getImportPhotoStableKey(photoRef: string, fallback: string): string {
  return getStorageStableKey(photoRef, fallback, IMPORT_PHOTO_BUCKET);
}

function getCachedSignedImportPhotoUrl(storagePath: string): string | null {
  const cached = signedImportPhotoUrlCache.get(storagePath);
  if (!cached) return null;

  if (cached.expiresAtMs - Date.now() <= IMPORT_PHOTO_SIGNED_URL_REFRESH_MARGIN_MS) {
    signedImportPhotoUrlCache.delete(storagePath);
    return null;
  }

  return cached.signedUrl;
}

function cacheSignedImportPhotoUrl(storagePath: string, signedUrl: string) {
  signedImportPhotoUrlCache.set(storagePath, {
    signedUrl,
    expiresAtMs: Date.now() + IMPORT_PHOTO_SIGNED_URL_TTL_SECONDS * 1000,
  });
}

export async function resolveImportPhotoUrl(photoRef: string): Promise<string> {
  const [resolved] = await resolveImportPhotoUrls([photoRef]);
  return resolved ?? photoRef;
}

export async function resolveImportPhotoThumbnailUrl(
  photoRef: string,
  options: { width?: number; height?: number; quality?: number } = {},
): Promise<string> {
  const width = options.width ?? 320;
  const height = options.height ?? 180;
  const quality = options.quality ?? 55;
  const storagePath = extractImportPhotoStoragePath(photoRef);
  if (!storagePath) return photoRef;
  return createSignedStorageThumbnailUrl(storagePath, {
    bucket: IMPORT_PHOTO_BUCKET,
    width,
    height,
    quality,
    resize: "cover",
    expiresIn: IMPORT_PHOTO_SIGNED_URL_TTL_SECONDS,
  });
}

export async function resolveImportPhotoUrls(photoRefs: string[]): Promise<string[]> {
  const refs = photoRefs.map((ref) => String(ref || "").trim()).filter(Boolean);
  if (refs.length === 0) return [];

  const resolved = [...refs];
  const entries = refs
    .map((ref, index) => ({ ref, index, storagePath: extractImportPhotoStoragePath(ref) }))
    .filter((entry): entry is { ref: string; index: number; storagePath: string } => Boolean(entry.storagePath));

  if (entries.length === 0) return resolved;

  const pathsToSign = Array.from(
    new Set(
      entries
        .map((entry) => {
          const cached = getCachedSignedImportPhotoUrl(entry.storagePath);
          if (cached) {
            resolved[entry.index] = cached;
            return null;
          }
          return entry.storagePath;
        })
        .filter((path): path is string => Boolean(path)),
    ),
  );

  if (pathsToSign.length === 0) return resolved;

  const signedUrls = await Promise.all(
    pathsToSign.map((storagePath) =>
      createSignedStorageFullImageUrl(storagePath, {
        bucket: IMPORT_PHOTO_BUCKET,
        expiresIn: IMPORT_PHOTO_SIGNED_URL_TTL_SECONDS,
      }),
    ),
  );

  signedUrls.forEach((signedUrl, dataIndex) => {
    const storagePath = pathsToSign[dataIndex];
    if (storagePath && signedUrl) cacheSignedImportPhotoUrl(storagePath, signedUrl);
  });

  entries.forEach((entry) => {
    const cached = getCachedSignedImportPhotoUrl(entry.storagePath);
    if (cached) resolved[entry.index] = cached;
  });

  return resolved;
}
